import {
  Button,
  HStack,
  Link,
  MenuItem,
  useDisclosure,
} from "@chakra-ui/react";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useFeeChargeEventConfirmDelete } from "features/fees/FeeChargeEventDelete";
import { OffsetApplicationsTableWithAllocationsDrawer } from "features/fees/offsets/FeeOffsetApplicationsTableWithAllocationsDrawer";
import { useConfirmDeleteDraftLateOffsetsAdjustmentEvent } from "features/fees/post/apply-offsets/FeePostReviewLateOffsetsPage";
import { LateOffsetsDateModal } from "features/fees/post/apply-offsets/LateOffsetsDateModal";
import {
  getFeeChargeEventListQueryKey,
  getFeeChargeEventPeriodBreakdownTableDataRetrieveQueryKey,
  getFeeChargeEventRetrieveQueryKey,
  getFeePeriodTrueUpSummaryTablesRetrieveQueryKey,
  useFeeChargeEventRetrieveSuspense,
  useFeeOffsetTableDataForFeeChargeEventRetrieve,
  useFeePeriodTrueUpSummaryTablesRetrieveSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import {
  getFundFamilyConfigRetrieveQueryKey,
  useFundFamilyConfigRetrieveSuspense,
} from "gen/endpoints/fund-family-config/fund-family-config";
import {
  investmentManagerDataRetrieve,
  useFundFamilyEventRetrieve,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FeeChargeEventPageDataDraftLateOffsetAdjustmentEventId } from "gen/model/feeChargeEventPageDataDraftLateOffsetAdjustmentEventId";
import { FundFamilyEventAPIData } from "gen/model/fundFamilyEventAPIData";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { GeneralLedgerTypeEnum } from "gen/model/generalLedgerTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { TFunction } from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Outlet } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import Alert from "shared/components/alert/Alert";
import { OutOfDateAlert } from "shared/components/alert/OutOfDateAlert";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import {
  PageHeader,
  PageHeaderBadge,
} from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxPageTabs } from "shared/components/table/box/TableBoxPageTabs";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useToast } from "shared/components/toast/useToast";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { ArrowUpRightIcon } from "shared/icons/ArrowUpRightIcon";
import { rootRoute } from "shared/navigation/Routes";
import { useCurrentPageTabId } from "shared/navigation/useCurrentPageTabId";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { downloadFileBlob, hyphensToUnderscores } from "shared/utils/utils";

const getFeeChargeEventBadge = (
  t: TFunction<["fees", "common", "capitalActivity"]>,
  fundFamilyEventData: FundFamilyEventAPIData | undefined,
  draftLateOffsetAdjustmentEventId: FeeChargeEventPageDataDraftLateOffsetAdjustmentEventId,
): PageHeaderBadge | undefined => {
  if (!fundFamilyEventData) {
    return undefined;
  }

  switch (fundFamilyEventData.state) {
    case FundFamilyEventStateMachineFieldsEnum.draft:
      return {
        text: t("common:draft"),
        color: "warning",
      };
    case FundFamilyEventStateMachineFieldsEnum.out_of_date:
      return {
        text: t("common:outOfDate"),
        color: "error",
      };
    case FundFamilyEventStateMachineFieldsEnum.completed:
      return draftLateOffsetAdjustmentEventId
        ? {
            text: t("fees:lateOffsets.draftOffsets"),
            color: "warning",
          }
        : {
            text: t("common:posted"),
            color: "success",
          };
    default:
      return undefined;
  }
};

export const PostedFeeChargePage: React.FC = () => {
  const { t } = useTranslation(["fees", "common", "capitalActivity"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invalidateQueryKeys = useInvalidateQueryKeys();
  const { showErrorToast } = useToast();

  const { fundFamilyId, feeChargeEventId } = useRequiredStringParams();
  const currentTabId = useCurrentPageTabId();

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data: feeChargeEventData, refetch: refetchFeeChargeEventData } =
    useFeeChargeEventRetrieveSuspense(fundFamilyId, feeChargeEventId);

  const params = {
    is_equalized_view: isEqualizedView || undefined,
  };

  const {
    data: feeOffsetApplicationsTableData,
    refetch: refetchFeeOffsetApplicationsTableData,
  } = useFeeOffsetTableDataForFeeChargeEventRetrieve(
    fundFamilyId,
    feeChargeEventId,
    params,
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const { data: trueUpSummaryTables } =
    useFeePeriodTrueUpSummaryTablesRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
    );

  const fundFamilyEventId =
    feeChargeEventData.fee_charge_event.fund_family_event_id;

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    fundFamilyEventId!,
    {
      query: {
        enabled: !!fundFamilyEventId,
        placeholderData: keepPreviousData,
      },
    },
  );

  // used to delete draft late offsets
  const confirmDeleteDraftLateOffsetsAdjustmentEvent =
    useConfirmDeleteDraftLateOffsetsAdjustmentEvent(
      fundFamilyId,
      feeChargeEventId,
    );

  const confirmDeleteFeeChargeEvent = useFeeChargeEventConfirmDelete(
    fundFamilyId,
    feeChargeEventId,
  );

  const {
    isOpen: isLateOffsetsDateModalOpen,
    onOpen: openLateOffsetsDateModal,
    onClose: closeLateOffsetsDateModal,
  } = useDisclosure();

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const isDraftEvent =
    fundFamilyEventData?.state === FundFamilyEventStateMachineFieldsEnum.draft;
  const isOutOfDateEvent =
    fundFamilyEventData?.state ===
    FundFamilyEventStateMachineFieldsEnum.out_of_date;
  const isLatestFeeChargeEvent = feeChargeEventData.is_latest_fee_charge_event;
  const allowLateOffsets =
    !isDraftEvent && !isOutOfDateEvent && isLatestFeeChargeEvent;
  const draftLateOffsetAdjustmentEventId =
    feeChargeEventData.draft_late_offset_adjustment_event_id;

  const showOffsetsTable = !!feeOffsetApplicationsTableData?.rows.length;
  const showCatchUpSummaryTable =
    !isEqualizedView &&
    !!trueUpSummaryTables.management_fee_catch_up_summary_table_data.rows
      .length;
  const showHistoricalBasisTrueUpSummaryTable =
    !isEqualizedView &&
    !!trueUpSummaryTables.historical_basis_fee_true_up_summary_table_data.rows
      .length;
  const showCancelCorrectSummaryTable =
    !isEqualizedView &&
    !!trueUpSummaryTables.cancel_correct_fee_adjustments_summary_table_data.rows
      .length;
  const showYardiExport =
    !isEqualizedView &&
    !allowLateOffsets &&
    !isOutOfDateEvent &&
    !isDraftEvent &&
    fundFamilyConfig.config.general_ledger_type == GeneralLedgerTypeEnum.yardi;

  const numberOfTrueUpTables = [
    showCatchUpSummaryTable,
    showHistoricalBasisTrueUpSummaryTable,
    showCancelCorrectSummaryTable,
  ].filter((x) => x).length;

  // if only one true up summary table, show it at 50% width
  const trueUpTableBoxProps =
    numberOfTrueUpTables === 1 ? { sx: { width: "50%" } } : { sx: { flex: 1 } };

  const onRefresh = async () => {
    await invalidateQueryKeys(
      getFeeChargeEventRetrieveQueryKey(fundFamilyId, feeChargeEventId),
      getFeePeriodTrueUpSummaryTablesRetrieveQueryKey(
        fundFamilyId,
        feeChargeEventId,
      ),
      getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
    );
  };

  const onDiscard = async () => {
    await queryClient.invalidateQueries({
      queryKey: getFeeChargeEventListQueryKey(fundFamilyId),
    });
    navigate(
      rootRoute({}).fundFamilies({}).fundFamily({ fundFamilyId }).fees({}).$,
    );
  };

  const downloadExport = async () => {
    const date = hyphensToUnderscores(fundFamilyEventData!.effective_date);
    const filename = `yardi_export_charge_fee_${date}.xlsx`;
    if (fundFamilyEventId) {
      try {
        const excelExportBlob = await investmentManagerDataRetrieve(
          fundFamilyId,
          fundFamilyEventId,
        );
        downloadFileBlob(excelExportBlob, filename);
      } catch (err) {
        showErrorToast(err);
      }
    }
  };

  const refetch = async () =>
    Promise.allSettled([
      refetchFeeChargeEventData(),
      refetchFeeOffsetApplicationsTableData(),
      queryClient.refetchQueries({
        queryKey: [
          getFeeChargeEventPeriodBreakdownTableDataRetrieveQueryKey(
            fundFamilyId,
            feeChargeEventId,
            params,
          ),
        ],
      }),
    ]);

  const deleteDraftOffsets = async () => {
    if (!draftLateOffsetAdjustmentEventId) return;
    await confirmDeleteDraftLateOffsetsAdjustmentEvent({
      adjustmentFundFamilyEventId: draftLateOffsetAdjustmentEventId,
    });
    await refetch();
  };

  return (
    <PageContent isFullHeight={false}>
      <PageHeader
        title={t("fees:managementFeesForDate", {
          date: feeChargeEventData.fee_charge_daterange_rich_name,
        })}
        backButton={{
          title: t("fees:fees"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .fees({}).$,
        }}
        badge={getFeeChargeEventBadge(
          t,
          fundFamilyEventData,
          draftLateOffsetAdjustmentEventId,
        )}
        actions={
          <>
            {showYardiExport && (
              <PageMenu>
                <MenuItem onClick={downloadExport}>
                  {t("capitalActivity:thirdPartyExports.yardiDownload")}
                </MenuItem>
              </PageMenu>
            )}
            {isDraftEvent && (
              <>
                <Button
                  colorScheme="secondary"
                  size="sm"
                  onClick={() =>
                    navigate(
                      rootRoute({})
                        .fundFamilies({})
                        .fundFamily({ fundFamilyId })
                        .fees({})
                        .post({ feeChargeEventId }).$,
                    )
                  }
                >
                  {t("fees:reviewAndPostFees")}
                </Button>
                <PageMenu>
                  <MenuItem
                    className="destructive"
                    onClick={confirmDeleteFeeChargeEvent}
                  >
                    {t("common:delete")}
                  </MenuItem>
                </PageMenu>
              </>
            )}
            {allowLateOffsets &&
              (!draftLateOffsetAdjustmentEventId ? (
                <Restricted permission={PermissionTypeEnum.write}>
                  <PageMenu>
                    <MenuItem onClick={openLateOffsetsDateModal}>
                      {t("fees:lateOffsets.addOffsets")}
                    </MenuItem>
                    <MenuItem onClick={() => downloadExport()}>
                      {t("capitalActivity:thirdPartyExports.yardiDownload")}
                    </MenuItem>
                  </PageMenu>
                </Restricted>
              ) : (
                <Restricted permission={PermissionTypeEnum.write}>
                  <PageMenu>
                    <MenuItem
                      onClick={() =>
                        navigate(
                          rootRoute({})
                            .fundFamilies({})
                            .fundFamily({ fundFamilyId })
                            .fees({})
                            .lateOffsets({
                              feeChargeEventId,
                              adjustmentFundFamilyEventId:
                                draftLateOffsetAdjustmentEventId,
                            })
                            .applyOffsets({}).$,
                        )
                      }
                    >
                      {t("fees:lateOffsets.reviewDraftOffsets")}
                    </MenuItem>
                    <MenuItem
                      className="destructive"
                      onClick={deleteDraftOffsets}
                    >
                      {t("fees:lateOffsets.deleteDraftOffsets")}
                    </MenuItem>
                  </PageMenu>
                </Restricted>
              ))}
          </>
        }
      />

      <PageBody>
        {draftLateOffsetAdjustmentEventId && (
          <Alert
            mb="16px"
            description={t("fees:lateOffsets.draftOffsetsWarning")}
            primaryAction={{
              title: t("fees:lateOffsets.reviewDraftOffsets"),
              to: rootRoute({})
                .fundFamilies({})
                .fundFamily({ fundFamilyId })
                .fees({})
                .lateOffsets({
                  feeChargeEventId,
                  adjustmentFundFamilyEventId: draftLateOffsetAdjustmentEventId,
                })
                .applyOffsets({}).$,
            }}
            secondaryAction={{
              title: t("fees:lateOffsets.deleteDraftOffsets"),
              onClick: deleteDraftOffsets,
            }}
          />
        )}
        {fundFamilyEventId && fundFamilyEventData?.out_of_date_reason && (
          <OutOfDateAlert
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            message={fundFamilyEventData.out_of_date_reason}
            onDiscard={onDiscard}
            onRefresh={onRefresh}
          />
        )}
        <TableBox key={currentTabId} isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxPageTabs />
            <DataTableBoxActions>
              <EqualizedViewToggleButton
                isEqualizedView={isEqualizedView}
                setIsEqualizedView={setIsEqualizedView}
                size="lg"
                actualTooltip={t("fees:actualTooltip")}
                equalizedTooltip={t("fees:equalizedTooltip")}
              />
            </DataTableBoxActions>
          </TableBoxHeader>
          <TableBoxBody>
            <QueryBoundary>
              <Outlet
                context={{ fundFamilyId, feeChargeEventId, isEqualizedView }}
              />
            </QueryBoundary>
          </TableBoxBody>
        </TableBox>
        {showOffsetsTable && (
          <OffsetApplicationsTableWithAllocationsDrawer
            feeOffsetApplicationsTableData={feeOffsetApplicationsTableData}
          />
        )}
        <HStack>
          {showCatchUpSummaryTable && (
            <TableBox boxProps={trueUpTableBoxProps}>
              <TableBoxHeader>
                <TableBoxTitle>{t("fees:managementFeeCatchUp")}</TableBoxTitle>
                <TableBoxActions>
                  <Link
                    colorScheme="primary"
                    size="sm"
                    style={{ textDecoration: "none" }}
                    onClick={() =>
                      navigate(
                        rootRoute({})
                          .fundFamilies({})
                          .fundFamily({ fundFamilyId })
                          .fees({})
                          .feeBreakdownManagementFeeCatchUp({
                            feeChargeEventId,
                          }).$,
                      )
                    }
                  >
                    {t("fees:trueUpPages.catchUpBreakdown")}
                    <ArrowUpRightIcon />
                  </Link>
                </TableBoxActions>
              </TableBoxHeader>
              <TableBoxBody>
                <DataTable
                  sx={{ ".ag-header": { display: "none" } }}
                  data={
                    trueUpSummaryTables.management_fee_catch_up_summary_table_data
                  }
                />
              </TableBoxBody>
            </TableBox>
          )}
          {showHistoricalBasisTrueUpSummaryTable && (
            <TableBox boxProps={trueUpTableBoxProps}>
              <TableBoxHeader>
                <TableBoxTitle>{t("fees:historicalBasisTrueUp")}</TableBoxTitle>
                <TableBoxActions>
                  <Link
                    colorScheme="primary"
                    size="sm"
                    style={{ textDecoration: "none" }}
                    onClick={() =>
                      navigate(
                        rootRoute({})
                          .fundFamilies({})
                          .fundFamily({ fundFamilyId })
                          .fees({})
                          .feeBreakdownHistoricalBasisFeeTrueUp({
                            feeChargeEventId,
                          }).$,
                      )
                    }
                  >
                    {t("fees:trueUpPages.trueUpBreakdown")}
                    <ArrowUpRightIcon />
                  </Link>
                </TableBoxActions>
              </TableBoxHeader>
              <TableBoxBody>
                <DataTable
                  sx={{ ".ag-header": { display: "none" } }}
                  data={
                    trueUpSummaryTables.historical_basis_fee_true_up_summary_table_data
                  }
                />
              </TableBoxBody>
            </TableBox>
          )}
          {showCancelCorrectSummaryTable && (
            <TableBox boxProps={trueUpTableBoxProps}>
              <TableBoxHeader>
                <TableBoxTitle>
                  {t("fees:cancelCorrectAdjustment")}
                </TableBoxTitle>
                <TableBoxActions>
                  <Link
                    colorScheme="primary"
                    size="sm"
                    style={{ textDecoration: "none" }}
                    onClick={() =>
                      navigate(
                        rootRoute({})
                          .fundFamilies({})
                          .fundFamily({ fundFamilyId })
                          .fees({})
                          .feeBreakdownCancelCorrectFeeAdjustments({
                            feeChargeEventId,
                          }).$,
                      )
                    }
                  >
                    {t("fees:trueUpPages.cancelCorrectFeeAdjustmentsBreakdown")}
                    <ArrowUpRightIcon />
                  </Link>
                </TableBoxActions>
              </TableBoxHeader>
              <TableBoxBody>
                <DataTable
                  sx={{ ".ag-header": { display: "none" } }}
                  data={
                    trueUpSummaryTables.cancel_correct_fee_adjustments_summary_table_data
                  }
                />
              </TableBoxBody>
            </TableBox>
          )}
          {isLateOffsetsDateModalOpen && (
            <LateOffsetsDateModal
              fundFamilyId={fundFamilyId}
              feeChargeEventId={feeChargeEventId}
              feeChargeDate={feeChargeEventData.transaction_date}
              onCloseComplete={closeLateOffsetsDateModal}
            />
          )}
        </HStack>
      </PageBody>
    </PageContent>
  );
};
